/**
 * PromptMySite Widget - Core Widget Class
 *
 * The main widget class that handles:
 * - Widget initialization and mounting
 * - Theme application
 * - Panel management (open/close/toggle)
 * - Workspace switching
 * - Mode switching (overlay/immersive)
 */

import { mergeConfig, type WidgetConfig, type WorkspaceConfig, type WidgetStyle, type WidgetPosition } from '../core/config';
import { createBubble, setBubbleOpen, hideBubbleBadge, destroyBubble } from '../ui/bubble';
import { ChatPanel } from '../ui/panel';
import { ApiClient, type WorkspaceSummary } from '../core/api-client';
import { inlineLoaderHTML, setLoaderContext } from '../core/loading/activity-loader';
import { debug, warn } from '../core/logger';
import widgetStyles from '../styles/widget.scss?inline';
import { registerBlockRenderer, getRegisteredBlockTypes, hasBlockRenderer, getBlockRenderer, type BlockRenderer } from '../ui/blocks';
import { clearAllFlowCache } from '../ui/blocks/flow-form';
import { resetDetailStore } from '../core/detail-store';
import { SplitPanelContainer } from '../ui/split-panel';
// MOTHBALLED: API Discovery and endpoint tool execution disabled for delivery simplification.
// import { ApiDiscoveryService, type DiscoveredEndpoint } from '../core/api-discovery';
// import { ApiExecutorService, type ToolRequest, type ToolResult } from '../core/api-executor';
import type { ToolRequest, ToolResult } from '../core/api-executor';
import { initI18n } from '../core/i18n';
import { loadCustomBlocks } from '../core/custom-block-loader';
import { PageObserver } from '../core/page-observer';
import { HostPagePusher } from '../core/host-page-pusher';
import { setPlatformFromConfig } from '../core/cart-integration';

/**
 * Widget always uses dark mode. Light mode auto-detection removed because
 * the dark theme looks consistently good on all host pages, while light mode
 * caused visual issues (white cards, washed-out blocks) on light-background stores.
 */
function detectPageMode(): 'dark' {
  return 'dark';
}

// =============================================================================
// WIDGET CLASS
// =============================================================================

export class PromptMySiteWidget {
  private config: WidgetConfig;
  private workspaceConfig: WorkspaceConfig | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private container: HTMLElement | null = null;
  private innerContainer: HTMLElement | null = null;
  private loadingOverlay: HTMLElement | null = null;
  private bubble: HTMLElement | null = null;
  private panel: ChatPanel | null = null;
  private splitContainer: SplitPanelContainer | null = null;
  private backdrop: HTMLElement | null = null;
  private pusher: HostPagePusher | null = null;
  private isOpen = false;
  private isSwitchingWorkspace = false;
  private apiClient: ApiClient;

  // MOTHBALLED: API Discovery and Executor services (types inlined as any since imports commented out)
  private discoveryService: any | null = null;
  private executorService: any | null = null;
  private lastReportedEndpointsHash = '';

  constructor(config: WidgetConfig) {
    this.config = config;
    this.apiClient = new ApiClient(config.apiUrl ?? 'https://api.promptmysite.com');
    // MOTHBALLED: Keep references to suppress TS6133 for mothballed methods
    void this._initializeToolExecutor;
    void this._initializeApiDiscovery;
    void this._handleDiscoveredEndpoints;
  }

  // ---------------------------------------------------------------------------
  // INITIALIZATION
  // ---------------------------------------------------------------------------

  /**
   * Initialize and mount the widget
   */
  async init(): Promise<void> {
    // Initialize i18n with configured language
    await initI18n({ language: this.config.language });

    await this.fetchWorkspaceConfig();

    // If workspace is disabled or doesn't exist, don't render anything
    if (this.workspaceNotFound) {
      return;
    }

    this.applyWorkspaceConfig();

    // Set loader context so activity loaders can show branded/vertical-specific animations
    // Script-tag vertical (this.config.vertical) takes priority; backend fills it during applyWorkspaceConfig
    setLoaderContext({
      vertical: this.config.vertical || this.workspaceConfig?.vertical,
      companyName: this.config.branding?.companyName,
    });

    // Resolve visibility: script tag override > backend config > default "visible"
    const visibility = this.config.visibility || this.workspaceConfig?.visibility || 'visible';

    // Resolve page observation: script tag override > backend config > default false
    const pageObservation = this.config.pageObservationEnabled
        ?? this.workspaceConfig?.pageObservationEnabled
        ?? false;

    // Start page observation if enabled (runs in both visible and hidden modes)
    if (pageObservation) {
      this.initializePageObserver();
    }

    // MOTHBALLED: API Discovery and endpoint tool execution disabled for delivery simplification.
    // await this.initializeToolExecutor();
    // await this.initializeApiDiscovery();

    if (visibility === 'hidden') {
      // Hidden mode: no UI rendered, observation only
      debug('Hidden mode — no UI will be rendered');
      return; // Skip mount() entirely
    }

    // Load custom blocks for this workspace (before rendering)
    const apiUrl = this.config.apiUrl ?? 'https://api.promptmysite.com';
    await loadCustomBlocks(apiUrl, this.config.workspaceId);

    this.mount();
  }

  /**
   * Fetch workspace configuration from backend
   */
  /** True when workspace fetch returned 404 (disabled or not found). */
  private workspaceNotFound = false;

  private async fetchWorkspaceConfig(): Promise<void> {
    try {
      this.workspaceConfig = await this.apiClient.getWorkspace(this.config.workspaceId);
      debug('Workspace config loaded:', this.workspaceConfig.name);
    } catch (error: any) {
      if (error?.statusCode === 404) {
        warn('Workspace not found or disabled — widget will not render');
        this.workspaceNotFound = true;
      } else {
        warn('Could not fetch workspace config, using defaults:', error);
      }
    }
  }

  /**
   * Apply workspace config to widget config
   * Local data attributes override backend config
   */
  private applyWorkspaceConfig(): void {
    if (!this.workspaceConfig) return;

    // Apply backend values only if not set locally
    if (!this.config.greeting && this.workspaceConfig.greeting) {
      this.config.greeting = this.workspaceConfig.greeting;
    }
    if (!this.config.placeholder && this.workspaceConfig.placeholder) {
      this.config.placeholder = this.workspaceConfig.placeholder;
    }
    if (!this.config.sampleQuestions && this.workspaceConfig.sampleQuestions) {
      this.config.sampleQuestions = this.workspaceConfig.sampleQuestions;
    }
    if (this.workspaceConfig.greetingBlocks && this.workspaceConfig.greetingBlocks.length > 0) {
      this.config.greetingBlocks = this.workspaceConfig.greetingBlocks;
    }

    // Merge theme (backend as base, local overrides)
    const backendTheme = this.workspaceConfig.theme ?? {};
    if (this.workspaceConfig.accentColor && !backendTheme.accentPrimary) {
      backendTheme.accentPrimary = this.workspaceConfig.accentColor;
    }
    this.config.theme = {
      ...backendTheme,
      ...this.config.theme,
    };

    // Apply branding from backend
    if (this.workspaceConfig.branding) {
      this.config.branding = {
        ...this.workspaceConfig.branding,
        ...this.config.branding,
      };
    }

    // Apply features from backend
    if (this.workspaceConfig.features) {
      this.config.features = {
        ...this.workspaceConfig.features,
      };
    }

    // Apply currency from backend
    if (this.workspaceConfig.currencyCode) {
      this.config.currencyCode = this.workspaceConfig.currencyCode;
    }
    if (this.workspaceConfig.currencySymbol) {
      this.config.currencySymbol = this.workspaceConfig.currencySymbol;
    }

    // Apply available flows from backend
    if (this.workspaceConfig.availableFlows) {
      this.config.availableFlows = this.workspaceConfig.availableFlows;
    }

    // Apply quick responses from backend
    if (this.workspaceConfig.quickResponses) {
      this.config.quickResponses = this.workspaceConfig.quickResponses;
    }

    // Apply autoOpen from backend (local override takes precedence)
    if (this.config.autoOpen === undefined && this.workspaceConfig.autoOpen !== undefined) {
      this.config.autoOpen = this.workspaceConfig.autoOpen;
    }

    // Apply vertical from backend
    if (this.workspaceConfig.vertical) {
      this.config.vertical = this.workspaceConfig.vertical;
    }

    // Apply color palette from backend
    if (this.workspaceConfig.colorPalette) {
      this.config.colorPalette = this.workspaceConfig.colorPalette;
    }

    // Apply custom surface/background color from backend
    if (this.workspaceConfig.surfaceColor) {
      this.config.surfaceColor = this.workspaceConfig.surfaceColor;
    }

    // Apply KB language hint from backend (used as language detection fallback)
    if (this.workspaceConfig.kbLanguage) {
      this.config.kbLanguage = this.workspaceConfig.kbLanguage;
    }

    // Apply e-commerce platform from backend for cart integration
    if (this.workspaceConfig.ecommercePlatform) {
      setPlatformFromConfig(this.workspaceConfig.ecommercePlatform);
    }

    // Apply widget style and position from backend (backend is source of truth).
    // Always applies — data-attributes on the script tag may be stale snapshots
    // from install time. The backend reflects the merchant's current settings.
    if (this.workspaceConfig.widgetStyle) {
      const style = this.workspaceConfig.widgetStyle as WidgetStyle;
      if (['bubble', 'vertical', 'inline', 'modal', 'immersive', 'overlay'].includes(style)) {
        this.config.style = style;
      }
    }
    if (this.workspaceConfig.widgetPosition) {
      const pos = this.workspaceConfig.widgetPosition as WidgetPosition;
      if (['bottom-right', 'bottom-left'].includes(pos)) {
        this.config.position = pos;
      }
    }

    // Apply bubble margins from backend (local data-attribute takes precedence)
    if (this.config.marginBottom === undefined && this.workspaceConfig.widgetMarginBottom !== undefined) {
      this.config.marginBottom = this.workspaceConfig.widgetMarginBottom;
    }
    if (this.config.marginSide === undefined && this.workspaceConfig.widgetMarginSide !== undefined) {
      this.config.marginSide = this.workspaceConfig.widgetMarginSide;
    }

    // Ensure script-tag mode overrides theme mode
    if (this.config.mode && this.config.theme) {
      this.config.theme.mode = this.config.mode;
    }
  }

  // ---------------------------------------------------------------------------
  // TOOL EXECUTOR & API DISCOVERY
  // ---------------------------------------------------------------------------

  /**
   * Initialize the tool executor service.
   * Always runs — workspaces may have pre-configured endpoints (seeded via
   * migration or admin) that need tool execution even without live discovery.
   */
  private async _initializeToolExecutor(): Promise<void> {
    // MOTHBALLED: Endpoint tool execution disabled for delivery simplification.
    // Original body:
    // try {
    //   this.executorService = new ApiExecutorService();
    //   const apiUrl = this.config.apiUrl ?? 'https://api.promptmysite.com';
    //   this.executorService.setBackendBaseUrl(apiUrl);
    //   await this.fetchApprovedEndpoints();
    //   debug('Tool executor initialized');
    // } catch (error) {
    //   console.error('[PMS] Failed to initialize tool executor:', error);
    // }
  }

  /**
   * Initialize API Discovery service if enabled in config.
   * Discovery observes network traffic to find new endpoints — this is
   * separate from the executor which runs already-known tool calls.
   */
  private async _initializeApiDiscovery(): Promise<void> {
    // MOTHBALLED: API Discovery disabled for delivery simplification.
    // Original body observes network traffic and reports discovered endpoints.
  }

  /**
   * Initialize the PageObserver for page content extraction.
   * Extracts DOM content from each page visitors browse and reports to backend.
   * Runs independently of widget visibility — works in both visible and hidden modes.
   */
  private initializePageObserver(): void {
    try {
      const observer = new PageObserver(this.apiClient, this.config);
      observer.start();
      debug('PageObserver initialized');
    } catch (error) {
      console.error('[PMS] Failed to initialize PageObserver:', error);
    }
  }

  /**
   * Handle discovered endpoints - report to orchestrator if changed
   */
  private _handleDiscoveredEndpoints(endpoints: any[]): void {
    // Create a hash to detect changes
    const hash = this.hashEndpoints(endpoints);
    if (hash === this.lastReportedEndpointsHash) {
      return; // No changes, skip report
    }

    this.lastReportedEndpointsHash = hash;
    this.reportDiscoveredEndpoints(endpoints);
  }

  /**
   * Create a simple hash of endpoints for change detection
   */
  private hashEndpoints(endpoints: any[]): string {
    const sorted = endpoints
      .map((ep) => `${ep.method} ${ep.urlPattern}`)
      .sort()
      .join('|');
    const pageCount = this.discoveryService?.getObservedPages().length ?? 0;
    return `${sorted}::pages=${pageCount}`;
  }

  /**
   * Report discovered endpoints to the orchestrator
   */
  private async reportDiscoveredEndpoints(endpoints: any[]): Promise<void> {
    if (endpoints.length === 0) return;

    const apiUrl = this.config.apiUrl ?? 'https://api.promptmysite.com';

    try {
      const response = await fetch(
        `${apiUrl}/api/workspaces/${this.workspaceConfig?.id ?? this.config.workspaceId}/discovery`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoints,
            pages: this.discoveryService?.getObservedPages() ?? [],
          }),
        }
      );

      if (!response.ok) {
        warn('Failed to report endpoints:', response.status);
      } else {
        debug('Reported', endpoints.length, 'discovered endpoints');
      }
    } catch (error) {
      warn('Error reporting discovered endpoints:', error);
    }
  }

  /**
   * Fetch approved endpoints from orchestrator and configure executor.
   * Uses workspace UUID (from config fetch) so the discovery controller
   * resolves correctly even before slug support is deployed.
   */
  private async fetchApprovedEndpoints(): Promise<void> {
    if (!this.executorService) return;

    const apiUrl = this.config.apiUrl ?? 'https://api.promptmysite.com';
    // Prefer the UUID from workspace config (fetched during init) over
    // the slug from the script tag — the discovery controller needs a UUID.
    const wsIdentifier = this.workspaceConfig?.id ?? this.config.workspaceId;

    try {
      const response = await fetch(
        `${apiUrl}/api/workspaces/${wsIdentifier}/discovery/endpoints/approved`
      );

      if (response.ok) {
        const data = await response.json();
        // Backend returns array directly, not wrapped in { endpoints: [...] }
        const approved = (Array.isArray(data) ? data : data.endpoints ?? []) as Array<{ urlPattern: string; method: string }>;
        this.executorService.setApprovedEndpoints(approved);
        debug('Loaded', approved.length, 'approved endpoints');
      } else if (response.status === 404) {
        // Endpoint not implemented yet, that's ok
        debug('Approved endpoints endpoint not found, using empty list');
        this.executorService.setApprovedEndpoints([]);
      } else {
        warn('Failed to fetch approved endpoints:', response.status);
      }
    } catch (error) {
      warn('Error fetching approved endpoints:', error);
      // Continue with empty approved list
      this.executorService.setApprovedEndpoints([]);
    }
  }

  /**
   * Execute a tool request (called by streaming client).
   *
   * Note: ApiExecutorService.execute() handles all errors internally and
   * always returns a ToolResult, so no try/catch wrapper is needed here.
   */
  async executeToolRequest(request: ToolRequest): Promise<ToolResult> {
    if (!this.executorService) {
      return {
        toolUseId: request.toolUseId,
        toolName: request.toolName,
        result: 'API Executor not initialized',
        isError: true,
        errorType: 'initialization',
      };
    }

    // Propagate sessionId from the tool request to the executor config
    // so it's available as a fallback for future proxy requests
    if (request.sessionId) {
      this.executorService.setSessionId(request.sessionId);
    }

    return this.executorService.execute(request);
  }

  /**
   * Get the executor service for use by ChatPanel
   */
  getExecutorService(): any | null {
    return this.executorService;
  }

  // ---------------------------------------------------------------------------
  // THEME APPLICATION
  // ---------------------------------------------------------------------------

  /**
   * Apply theme settings to the container element
   */
  private applyThemeToContainer(): void {
    if (!this.innerContainer) return;

    const theme = this.config.theme;

    // Clear old theme attributes
    this.clearThemeAttributes();

    if (!theme) return;

    // Widget always uses dark mode — light mode removed.

    // Theme preset
    if (theme.preset && theme.preset !== 'default' && theme.preset !== 'custom') {
      this.innerContainer.setAttribute('data-theme', theme.preset);
    }

    // Vertical attribute for industry-specific styling (lowercase for CSS selector matching)
    if (this.workspaceConfig?.vertical) {
      this.innerContainer.setAttribute('data-vertical', this.workspaceConfig.vertical.toLowerCase());
    }

    // Color palette override for vertical accent colors
    if (this.config.colorPalette) {
      this.innerContainer.setAttribute('data-color-palette', this.config.colorPalette);
    }

    // Core colors
    this.applyColorTheme(theme);

    // Typography
    if (theme.fontFamily) {
      this.innerContainer.style.setProperty('--pms-font', theme.fontFamily);
    }

    // Layout
    this.applyLayoutTheme(theme);

    // Background
    this.applyBackgroundTheme(theme);

    // Header
    this.applyHeaderTheme(theme);

    // Messages
    this.applyMessageTheme(theme);

    // Effects
    this.applyEffectsTheme(theme);

    // Panel & Input
    this.applyPanelTheme(theme);

    // Interactive elements
    this.applyInteractiveTheme(theme);

    // Polish
    this.applyPolishTheme(theme);

    // Color palette overrides (must come after all theme methods to override inline styles)
    this.applyPaletteOverrides();

    // Custom AI-generated CSS (premium feature)
    this.applyCustomStyleCss();
  }

  private clearThemeAttributes(): void {
    if (!this.innerContainer) return;

    const attrs = [
      'data-theme', 'data-mode', 'data-color-palette', 'data-has-bg-image', 'data-header-logo',
      'data-bubble-style', 'data-message-spacing', 'data-glow', 'data-animation',
      'data-shadow', 'data-button-style', 'data-card-style', 'data-link-style',
      'data-chip-style', 'data-hover', 'data-click', 'data-transition',
      'data-focus', 'data-scrollbar', 'data-skeleton', 'data-divider'
    ];

    attrs.forEach(attr => this.innerContainer!.removeAttribute(attr));
  }

  private applyColorTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    // When a color palette is active, skip inline accent styles so the
    // CSS [data-color-palette] rules can take effect without being
    // overridden by inline styles. applyPaletteOverrides() handles the rest.
    if (this.config.colorPalette && PromptMySiteWidget.PALETTE_COLORS[this.config.colorPalette]) {
      return;
    }

    const color = theme.accentPrimary || theme.primaryColor;
    if (color) {
      this.innerContainer.style.setProperty('--pms-neon-cyan', color);
      this.innerContainer.style.setProperty('--pms-primary', color);
      // Keep --pms-primary-rgb in sync for rgba() usage elsewhere
      const rgb = PromptMySiteWidget.hexToRgb(color);
      if (rgb) {
        this.innerContainer.style.setProperty('--pms-primary-rgb', rgb);
      }
      this.innerContainer.style.setProperty('--pms-gradient-button',
        `linear-gradient(135deg, ${theme.accentSecondary || color} 0%, ${color} 100%)`
      );
    }

    if (theme.accentSecondary) {
      this.innerContainer.style.setProperty('--pms-neon-purple', theme.accentSecondary);
    }
  }

  private applyLayoutTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.borderRadius) {
      this.innerContainer.style.setProperty('--pms-border-radius', theme.borderRadius);
    }
    if (theme.panelWidth) {
      this.innerContainer.style.setProperty('--pms-panel-width', theme.panelWidth);
    }
    if (theme.panelHeight) {
      this.innerContainer.style.setProperty('--pms-panel-height', theme.panelHeight);
    }
  }

  private applyBackgroundTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.backgroundImage) {
      this.innerContainer.style.setProperty('--pms-bg-image', `url(${theme.backgroundImage})`);
      this.innerContainer.setAttribute('data-has-bg-image', 'true');
    }
    if (theme.backgroundOverlay) {
      this.innerContainer.style.setProperty('--pms-bg-overlay', theme.backgroundOverlay);
    }
    if (theme.backgroundBlur) {
      this.innerContainer.style.setProperty('--pms-bg-blur', theme.backgroundBlur);
    }
    if (theme.backgroundPosition) {
      this.innerContainer.style.setProperty('--pms-bg-position', theme.backgroundPosition);
    }
    if (theme.backgroundSize) {
      this.innerContainer.style.setProperty('--pms-bg-size', theme.backgroundSize);
    }
  }

  private applyHeaderTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.headerBackground) {
      this.innerContainer.style.setProperty('--pms-header-bg', theme.headerBackground);
    }
    if (theme.headerBorderColor) {
      this.innerContainer.style.setProperty('--pms-header-border', theme.headerBorderColor);
    }
    if (theme.headerLogo) {
      this.innerContainer.style.setProperty('--pms-header-logo', `url(${theme.headerLogo})`);
      this.innerContainer.setAttribute('data-header-logo', theme.headerLogo);
    }
    if (theme.headerHeight) {
      this.innerContainer.style.setProperty('--pms-header-height', theme.headerHeight);
    }
  }

  private applyMessageTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.messageBubbleStyle) {
      this.innerContainer.setAttribute('data-bubble-style', theme.messageBubbleStyle);
    }
    if (theme.messageSpacing) {
      this.innerContainer.setAttribute('data-message-spacing', theme.messageSpacing);
    }
  }

  private applyEffectsTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.glowIntensity) {
      this.innerContainer.setAttribute('data-glow', theme.glowIntensity);
    }
    if (theme.animationStyle) {
      this.innerContainer.setAttribute('data-animation', theme.animationStyle);
    }
  }

  private applyPanelTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.panelBorderColor) {
      this.innerContainer.style.setProperty('--pms-panel-border', theme.panelBorderColor);
    }
    if (theme.panelShadow) {
      this.innerContainer.setAttribute('data-shadow', theme.panelShadow);
    }
    if (theme.inputBackground) {
      this.innerContainer.style.setProperty('--pms-input-bg', theme.inputBackground);
    }
    if (theme.inputBorderColor) {
      this.innerContainer.style.setProperty('--pms-input-border', theme.inputBorderColor);
    }
  }

  private applyInteractiveTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.buttonStyle) {
      this.innerContainer.setAttribute('data-button-style', theme.buttonStyle);
    }
    if (theme.cardStyle) {
      this.innerContainer.setAttribute('data-card-style', theme.cardStyle);
    }
    if (theme.linkStyle) {
      this.innerContainer.setAttribute('data-link-style', theme.linkStyle);
    }
    if (theme.actionChipStyle) {
      this.innerContainer.setAttribute('data-chip-style', theme.actionChipStyle);
    }
    if (theme.hoverEffect) {
      this.innerContainer.setAttribute('data-hover', theme.hoverEffect);
    }
    if (theme.clickFeedback) {
      this.innerContainer.setAttribute('data-click', theme.clickFeedback);
    }
    if (theme.contentTransition) {
      this.innerContainer.setAttribute('data-transition', theme.contentTransition);
    }
    if (theme.focusRingStyle) {
      this.innerContainer.setAttribute('data-focus', theme.focusRingStyle);
    }
  }

  private applyPolishTheme(theme: NonNullable<WidgetConfig['theme']>): void {
    if (!this.innerContainer) return;

    if (theme.scrollbarStyle) {
      this.innerContainer.setAttribute('data-scrollbar', theme.scrollbarStyle);
    }
    if (theme.scrollbarColor) {
      this.innerContainer.style.setProperty('--pms-scrollbar-color', theme.scrollbarColor);
    }
    if (theme.selectionColor) {
      this.innerContainer.style.setProperty('--pms-selection-color', theme.selectionColor);
    }
    if (theme.skeletonStyle) {
      this.innerContainer.setAttribute('data-skeleton', theme.skeletonStyle);
    }
    if (theme.dividerStyle) {
      this.innerContainer.setAttribute('data-divider', theme.dividerStyle);
    }
  }

  /** Convert hex color (#rgb or #rrggbb) to "r, g, b" string for rgba() usage */
  private static hexToRgb(hex: string): string | null {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (m?.[1] && m[2] && m[3]) return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
    const s = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    if (s?.[1] && s[2] && s[3]) return `${parseInt(s[1]+s[1], 16)}, ${parseInt(s[2]+s[2], 16)}, ${parseInt(s[3]+s[3], 16)}`;
    return null;
  }

  private static readonly PALETTE_COLORS: Record<string, { accent: string; accentDark: string; rgb: string }> = {
    cyan:   { accent: '#06b6d4', accentDark: '#0891b2', rgb: '6, 182, 212' },
    blue:   { accent: '#3b82f6', accentDark: '#1d4ed8', rgb: '59, 130, 246' },
    sky:    { accent: '#0ea5e9', accentDark: '#0284c7', rgb: '14, 165, 233' },
    gold:   { accent: '#c9a227', accentDark: '#a88520', rgb: '201, 162, 39' },
    amber:  { accent: '#f59e0b', accentDark: '#d97706', rgb: '245, 158, 11' },
    orange: { accent: '#F97316', accentDark: '#ea580c', rgb: '249, 115, 22' },
    red:    { accent: '#ef4444', accentDark: '#dc2626', rgb: '239, 68, 68' },
    rose:   { accent: '#f43f5e', accentDark: '#e11d48', rgb: '244, 63, 94' },
    green:  { accent: '#22c55e', accentDark: '#16a34a', rgb: '34, 197, 94' },
    teal:   { accent: '#14b8a6', accentDark: '#0d9488', rgb: '20, 184, 166' },
    purple: { accent: '#a855f7', accentDark: '#7c3aed', rgb: '168, 85, 247' },
  };

  /**
   * Override global theme inline styles with palette colors.
   * Runs after all theme methods so inline palette values take priority.
   * Also removes any leftover inline accent styles set by theme methods
   * on previous calls (e.g., workspace switch).
   */
  private applyPaletteOverrides(): void {
    if (!this.innerContainer || !this.config.colorPalette) return;

    const p = PromptMySiteWidget.PALETTE_COLORS[this.config.colorPalette];
    if (!p) return;

    const el = this.innerContainer;

    // Remove any inline accent styles from previous theme application
    // so the CSS [data-color-palette] rules take full effect.
    const accentVars = [
      '--pms-neon-cyan', '--pms-neon-purple', '--pms-primary', '--pms-primary-rgb',
      '--pms-primary-light', '--pms-gradient-button', '--pms-gradient-accent',
      '--pms-gradient-glow', '--pms-shadow-glow',
      '--pms-border-hover', '--pms-border-focus',
      '--pms-header-border', '--pms-panel-border', '--pms-input-border',
      '--pms-chat-user-bg', '--pms-user-bg',
      '--pms-scrollbar-color', '--pms-selection-color',
    ];
    for (const v of accentVars) {
      el.style.removeProperty(v);
    }

    // Apply custom surface color override (inline beats CSS palette defaults)
    if (this.config.surfaceColor) {
      el.style.setProperty('--pms-vertical-surface', this.config.surfaceColor);
      // Also override the main panel/container background
      el.style.setProperty('--pms-bg', this.config.surfaceColor);
    }
  }

  /**
   * Inject AI-generated custom CSS into the Shadow DOM.
   * Applied last so it overrides default + theme styles.
   *
   * When a color palette is active, color-related properties are stripped
   * from the custom CSS so they don't override the palette variable values.
   */
  private applyCustomStyleCss(): void {
    // Remove any existing custom style sheet
    const existingCustom = this.shadowRoot?.querySelector('style[data-custom-style]');
    if (existingCustom) {
      existingCustom.remove();
    }

    let customCss = this.workspaceConfig?.customStyleCss;
    if (!customCss || !this.shadowRoot) return;

    // When a color palette is active, strip color-related declarations
    // from custom CSS so palette variables take effect everywhere.
    if (this.config.colorPalette && PromptMySiteWidget.PALETTE_COLORS[this.config.colorPalette]) {
      customCss = customCss.replace(
        /\b(color|background-color|border-color|background|border|box-shadow|text-shadow|outline-color)\s*:[^;}{]*;/gi,
        '/* palette-override */'
      );
    }

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-custom-style', 'true');
    styleEl.textContent = customCss;
    this.shadowRoot.appendChild(styleEl);
  }

  // ---------------------------------------------------------------------------
  // DOM MOUNTING
  // ---------------------------------------------------------------------------

  /**
   * Mount the widget to the DOM
   */
  private mount(): void {
    // Create container with shadow DOM
    this.container = document.createElement('div');
    this.container.id = 'promptmysite-widget';
    this.container.setAttribute('aria-label', 'PromptMySite Chat Widget');
    // Prevent host themes (e.g. Shopify) from hiding the container via `div:empty { display: none }`
    // Shadow DOM children don't count as regular children, so the host element appears "empty" to CSS.
    this.container.style.display = 'block';

    // Attach shadow DOM
    this.shadowRoot = this.container.attachShadow({ mode: 'open' });

    // Load Google Fonts used by vertical themes
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Libre+Baskerville:wght@400;700&family=Merriweather:wght@400;700&family=Source+Sans+3:wght@400;600;700&display=swap';
    this.shadowRoot.appendChild(fontLink);

    // Inject styles
    const styleSheet = document.createElement('style');
    styleSheet.textContent = widgetStyles;
    this.shadowRoot.appendChild(styleSheet);

    // Create inner container
    this.innerContainer = document.createElement('div');
    this.innerContainer.className = 'pms-container';

    // Detect page background mode for adaptive styling
    const pageMode = detectPageMode();
    this.innerContainer.setAttribute('data-page-mode', pageMode);
    debug('Page mode detected:', pageMode);

    this.applyThemeToContainer();
    this.shadowRoot.appendChild(this.innerContainer);

    // Always create bubble (needed for runtime style switching)
    this.createBubble();

    // Create panel
    this.createPanel();

    // Create backdrop for overlay mode
    this.createBackdropIfNeeded();

    // Create loading overlay
    this.createLoadingOverlay();

    // Create host page pusher (default on unless explicitly disabled)
    if (this.config.pushContent !== false) {
      this.pusher = new HostPagePusher(this.container!);
    }

    // Pass pusher to panel / split container for drag sync
    if (this.pusher) {
      if (this.splitContainer) {
        this.splitContainer.setPusher(this.pusher);
      } else {
        this.panel?.setPusher(this.pusher);
      }
    }

    // Append to body
    document.body.appendChild(this.container);

    // Auto-open based on style
    this.handleAutoOpen();

    debug(' Widget initialized', {
      workspaceId: this.config.workspaceId,
      style: this.config.style,
      position: this.config.position,
    });
  }

  private createBubble(): void {
    this.bubble = createBubble(this.config, {
      onToggle: (isOpen) => this.togglePanel(isOpen),
    });
    this.innerContainer!.appendChild(this.bubble);
    // Bubble starts visible for all styles — it's the entry point.
    // open() hides it when the panel opens, close() shows it again.
  }

  private createPanel(): void {
    const panelCallbacks = {
      onClose: () => this.close(),
      onMessageSent: (message: string) => debug(' Message sent:', message),
      onResponseReceived: (response: string) => debug(' Response received:', response),
      onError: (error: Error) => console.error('[PromptMySite] Error:', error),
      onActionClick: (action: string) => {
        debug(' Action clicked:', action);
        if (this.shouldSwitchToSplitView(action)) {
          this.switchToImmersiveSplit();
        }
      },
      onDemoSwitch: (demoId: string) => {
        debug(' Switching to demo:', demoId);
        this.switchWorkspace(demoId);
      },
      // MOTHBALLED: Endpoint tool execution disabled for delivery simplification.
      onToolRequest: undefined,
      // Style switching (bubble ↔ immersive ↔ vertical)
      onStyleSwitch: (style: string) => {
        if (style === 'bubble') this.switchToBubble();
        else if (style === 'vertical') this.switchToVertical();
        else if (style === 'immersive') this.switchToImmersiveSplit();
      },
    };

    if (this.config.splitPanel?.enabled) {
      debug('[Widget] Creating SplitPanelContainer');
      this.splitContainer = new SplitPanelContainer(
        this.config,
        {
          ...panelCallbacks,
          onSummaryAction: (action, items) => debug(' Summary action:', action, items),
        },
        this.config.splitPanel
      );
      const containerElement = this.splitContainer.create();
      this.innerContainer!.appendChild(containerElement);
      this.panel = this.splitContainer.getChatPanel();

      // If starting in bubble mode, reparent panel out of split wrapper
      if (this.config.style === 'bubble') {
        this.splitContainer.enterBubbleMode();
      }
    } else {
      this.panel = new ChatPanel(this.config, panelCallbacks);
      const panelElement = this.panel.create();
      this.innerContainer!.appendChild(panelElement);
    }
  }

  private createBackdropIfNeeded(): void {
    // Backdrop disabled - was causing blur issues
  }

  private createLoadingOverlay(): void {
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.className = 'pms-loading-overlay';
    this.loadingOverlay.innerHTML = `
      ${inlineLoaderHTML('Loading workspace...')}
    `;
    this.innerContainer!.appendChild(this.loadingOverlay);
  }

  private handleAutoOpen(): void {
    // All styles respect autoOpen. When autoOpen is false (or unset),
    // the widget starts with the bubble visible and panel closed.
    // The user clicks the bubble to open into the configured style
    // (bubble overlay, vertical sidebar, or immersive full-page).
    if (this.config.autoOpen === true) {
      this.open();
    }
  }

  // ---------------------------------------------------------------------------
  // PANEL VISIBILITY
  // ---------------------------------------------------------------------------

  private togglePanel(isOpen: boolean): void {
    this.isOpen = isOpen;
    if (isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  open(): void {
    this.isOpen = true;
    if (this.splitContainer) {
      this.splitContainer.show();
    } else {
      this.panel?.show();
    }

    if (this.bubble) {
      setBubbleOpen(this.bubble, true);
      hideBubbleBadge(this.bubble);
      // Hide bubble when panel is open — all styles use bubble as entry point
      this.bubble.style.display = 'none';
    }

    if (this.backdrop) {
      this.backdrop.classList.add('pms-overlay-backdrop-visible');
    }

    // Push host page content if applicable
    this.applyPush();

    // Dispatch custom event for host page to react (e.g., blur effect)
    window.dispatchEvent(new CustomEvent('promptmysite:open'));
  }

  close(): void {
    this.isOpen = false;
    if (this.splitContainer) {
      this.splitContainer.hide();
    } else {
      this.panel?.hide();
    }

    if (this.bubble) {
      setBubbleOpen(this.bubble, false);
      // Show bubble again when panel is closed
      this.bubble.style.display = '';
    }

    if (this.backdrop) {
      this.backdrop.classList.remove('pms-overlay-backdrop-visible');
    }

    // Reset host page push
    this.pusher?.reset();

    // Dispatch custom event for host page to react (e.g., remove blur effect)
    window.dispatchEvent(new CustomEvent('promptmysite:close'));
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // ---------------------------------------------------------------------------
  // LOADING STATE
  // ---------------------------------------------------------------------------

  private showLoading(): void {
    this.loadingOverlay?.classList.add('pms-loading-visible');
  }

  private hideLoading(): void {
    this.loadingOverlay?.classList.remove('pms-loading-visible');
  }

  // ---------------------------------------------------------------------------
  // WORKSPACE SWITCHING
  // ---------------------------------------------------------------------------

  async switchWorkspace(workspaceId: string): Promise<void> {
    // Prevent concurrent workspace switches (click-spam protection)
    if (this.isSwitchingWorkspace) {
      debug(' Ignoring workspace switch - already switching');
      return;
    }
    this.isSwitchingWorkspace = true;

    debug(' Switching workspace to:', workspaceId);
    this.showLoading();

    // Clear flow request cache to prevent stale data from previous workspace
    clearAllFlowCache();
    // Clear detail store to prevent stale card details
    resetDetailStore();

    try {
      // Reset workspace-specific config
      this.config.workspaceId = workspaceId;
      this.config.greeting = undefined;
      this.config.placeholder = undefined;
      this.config.sampleQuestions = undefined;
      this.config.greetingBlocks = undefined;
      this.config.branding = undefined;
      this.config.features = undefined;
      if (this.config.theme) {
        this.config.theme.preset = undefined;
      }

      // Fetch and apply new config
      await this.fetchWorkspaceConfig();
      this.applyWorkspaceConfig();
      this.applyThemeToContainer();

      // Re-fetch approved endpoints for the new workspace
      await this.fetchApprovedEndpoints();

      // Destroy existing panel
      if (this.splitContainer) {
        this.splitContainer.destroy();
        this.splitContainer = null;
        this.panel = null;
      } else if (this.panel) {
        this.panel.destroy();
        this.panel = null;
      }

      // Remove old panel elements
      if (this.innerContainer) {
        this.innerContainer.querySelector('.pms-panel')?.remove();
        this.innerContainer.querySelector('.pms-split-container')?.remove();

        // Recreate panel
        this.createPanel();

        // Set shadow root for modals (createPanel just set containers or panel)
        if (this.shadowRoot) {
          if (this.splitContainer) {
            (this.splitContainer as SplitPanelContainer).setShadowRoot(this.shadowRoot);
          } else if (this.panel) {
            (this.panel as ChatPanel).setShadowRoot(this.shadowRoot);
          }
        }

        // Show panel if was open
        if (this.isOpen) {
          if (this.splitContainer) {
            (this.splitContainer as SplitPanelContainer).show();
          } else if (this.panel) {
            (this.panel as ChatPanel).show();
          }
        }
      }

      debug(' Workspace switched to:', workspaceId);
    } finally {
      this.hideLoading();
      this.isSwitchingWorkspace = false;
    }
  }

  // ---------------------------------------------------------------------------
  // HOST PAGE PUSH
  // ---------------------------------------------------------------------------

  /** Apply push based on current style and panel dimensions. */
  private applyPush(): void {
    if (!this.pusher) return;
    const style = this.config.style;

    if (style === 'vertical') {
      const panel = this.innerContainer?.querySelector('.pms-panel, .pms-split-container') as HTMLElement | null;
      const width = panel?.getBoundingClientRect().width ?? 420;
      this.pusher.pushRight(width);
    } else if (style === 'immersive') {
      const panel = this.innerContainer?.querySelector('.pms-panel-immersive, .pms-split-container.pms-split-immersive') as HTMLElement | null;
      const height = panel ? window.innerHeight - panel.getBoundingClientRect().top : Math.round(window.innerHeight * 0.2);
      this.pusher.pushBottom(height);
    }
    // bubble / overlay — no push
  }

  // ---------------------------------------------------------------------------
  // MODE SWITCHING
  // ---------------------------------------------------------------------------

  private shouldSwitchToSplitView(action: string): boolean {
    if (this.config.style === 'immersive') return false;
    if (!this.config.splitPanel?.enabled) return false;

    const splitTriggers = ['list:add', 'select:', 'view:', 'detail:', 'profile:', 'compare'];
    return splitTriggers.some(trigger => action.startsWith(trigger) || action.includes(trigger));
  }

  switchToImmersiveSplit(): void {
    if (this.config.style === 'immersive') return;

    debug(' Switching to immersive split view');
    this.config.style = 'immersive';

    // Exit bubble mode first (reparents panel back into split wrapper)
    this.splitContainer?.exitBubbleMode();

    if (this.innerContainer) {
      this.innerContainer.classList.add('pms-immersive-mode');
    }

    const splitContainer = this.innerContainer?.querySelector('.pms-split-container');
    splitContainer?.classList.add('pms-split-immersive');

    const panel = this.innerContainer?.querySelector('.pms-panel');
    if (panel) {
      // Remove bubble/overlay position classes
      panel.classList.remove('pms-panel-overlay', 'pms-panel-bottom-right', 'pms-panel-bottom-left');
      panel.classList.add('pms-panel-immersive', 'pms-panel-open');
    }

    if (this.backdrop) this.backdrop.style.display = 'none';
    if (this.bubble) this.bubble.style.display = 'none';

    // Clear any drag position from bubble mode
    this.panel?.resetDragPosition();

    // Show top handle (immersive feature)
    this.panel?.showTopHandle();

    // Push host page content
    this.applyPush();
  }

  switchToVertical(): void {
    if (this.config.style === 'vertical') return;

    debug(' Switching to vertical sidebar');
    this.config.style = 'vertical';

    // Exit bubble mode first (reparents panel back into split wrapper)
    this.splitContainer?.exitBubbleMode();

    if (this.innerContainer) {
      this.innerContainer.classList.add('pms-immersive-mode');
    }

    const splitContainer = this.innerContainer?.querySelector('.pms-split-container');
    splitContainer?.classList.add('pms-split-immersive');

    const panel = this.innerContainer?.querySelector('.pms-panel');
    if (panel) {
      panel.classList.remove('pms-panel-overlay', 'pms-panel-bottom-right', 'pms-panel-bottom-left', 'pms-panel-immersive');
      panel.classList.add('pms-panel-vertical', 'pms-panel-open');
    }

    if (this.backdrop) this.backdrop.style.display = 'none';
    if (this.bubble) this.bubble.style.display = 'none';

    // Clear any drag position from bubble mode
    this.panel?.resetDragPosition();

    // Show top handle and switch to vertical layout
    this.panel?.showTopHandle();
    this.panel?.setActiveLayout('vertical');

    // Push host page content
    this.applyPush();
  }

  switchToOverlay(): void {
    if (this.config.style === 'overlay') return;

    debug(' Switching to overlay mode');
    this.config.style = 'overlay';

    if (this.innerContainer) {
      this.innerContainer.classList.remove('pms-immersive-mode');
    }

    const splitContainer = this.innerContainer?.querySelector('.pms-split-container');
    splitContainer?.classList.remove('pms-split-immersive');

    const panel = this.innerContainer?.querySelector('.pms-panel');
    if (panel) {
      panel.classList.remove('pms-panel-immersive', 'pms-panel-bottom-right', 'pms-panel-bottom-left');
      panel.classList.add('pms-panel-overlay');
    }

    if (this.backdrop) this.backdrop.style.display = '';
    if (this.bubble) this.bubble.style.display = '';

    // Hide top handle (not needed for overlay)
    this.panel?.hideTopHandle();

    this.splitContainer?.closeSummary();
    this.pusher?.reset();
    document.body.style.overflow = '';
  }

  switchToBubble(): void {
    if (this.config.style === 'bubble') return;

    debug(' Switching to bubble mode');
    this.config.style = 'bubble';

    if (this.innerContainer) {
      this.innerContainer.classList.remove('pms-immersive-mode');
    }

    const splitContainer = this.innerContainer?.querySelector('.pms-split-container');
    splitContainer?.classList.remove('pms-split-immersive');

    const panel = this.innerContainer?.querySelector('.pms-panel');
    if (panel) {
      panel.classList.remove('pms-panel-immersive', 'pms-panel-vertical', 'pms-panel-overlay');
      const position = this.config.position ?? 'bottom-right';
      panel.classList.add(`pms-panel-${position}`, 'pms-panel-open');
    }

    // Keep panel open — hide the bubble since panel is visible
    if (this.bubble) {
      setBubbleOpen(this.bubble, true);
      this.bubble.style.display = 'none';
    }
    this.isOpen = true;

    // Hide top handle (bubble uses header instead)
    this.panel?.hideTopHandle();

    // Enter bubble mode on split container (reparents panel, hides wrapper)
    this.splitContainer?.enterBubbleMode();
    this.pusher?.reset();
    document.body.style.overflow = '';
  }

  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  /**
   * Explicitly set the user's authentication state.
   *
   * NOTE: In most cases, you don't need to call this - the widget auto-detects
   * auth state by checking localStorage for common token keys (accessToken,
   * authToken, jwt, etc.). Use `authTokenKeys` config to customize which keys
   * are checked.
   *
   * This method is for apps that don't use localStorage for tokens.
   *
   * @param isAuthenticated Whether the user is currently authenticated
   *
   * @example
   * ```js
   * // Explicit override (only if not using localStorage for tokens)
   * widget.setAuthState(true);
   * ```
   */
  setAuthState(isAuthenticated: boolean): void {
    this.config.isAuthenticated = isAuthenticated;
    debug(' Auth state explicitly set:', isAuthenticated);
  }

  destroy(): void {
    // Stop API Discovery
    if (this.discoveryService) {
      this.discoveryService.stop();
      this.discoveryService = null;
    }
    this.executorService = null;

    this.pusher?.reset();
    this.pusher = null;
    this.splitContainer?.destroy();
    this.panel?.destroy();
    if (this.bubble) {
      destroyBubble(this.bubble);
    }
    this.container?.remove();
    this.container = null;
    this.shadowRoot = null;
    this.bubble = null;
    this.panel = null;
    this.splitContainer = null;
    this.backdrop = null;
    document.body.style.overflow = '';
    debug(' Widget destroyed');
  }

  getConfig(): WidgetConfig {
    return { ...this.config };
  }

  async listWorkspaces(): Promise<WorkspaceSummary[]> {
    return this.apiClient.listWorkspaces();
  }

  // ---------------------------------------------------------------------------
  // STATIC METHODS
  // ---------------------------------------------------------------------------

  static registerBlock(type: string, renderer: BlockRenderer): void {
    registerBlockRenderer(type, renderer);
    debug(`[PromptMySite] Registered custom block: ${type}`);
  }

  static getBlockTypes(): string[] {
    return getRegisteredBlockTypes();
  }

  /**
   * Get access to the block registry for demos/debugging.
   * Returns a Map-like interface with has() and get() methods.
   */
  static get blockRegistry(): { has: (type: string) => boolean; get: (type: string) => BlockRenderer | undefined } {
    return {
      has: hasBlockRenderer,
      get: getBlockRenderer,
    };
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

export function createWidget(userConfig: Partial<WidgetConfig>): PromptMySiteWidget {
  const config = mergeConfig(userConfig);
  const widget = new PromptMySiteWidget(config);
  widget.init();
  return widget;
}
